import {
    DeleteOutlined, EditOutlined,
} from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Card, Col, Flex, Input, Pagination, Row, Skeleton, Tag, theme, Typography,
} from 'antd';
import {
    useCallback, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

import {
    myCommentsQueries, scriptCommentsMutations,
} from '@/api';
import { MyCommentItem } from '@/types';

const PER_PAGE = 15;

const MyCommentCard = ({ comment }: { comment: MyCommentItem }) => {
    const { token } = theme.useToken();
    const [editMode, setEditMode] = useState(false);
    const [editBody, setEditBody] = useState(comment.body);

    const scriptId = comment.script?.id ?? '';
    const canEdit = !comment.is_read;

    const { mutate: updateComment, isPending: isUpdating } = useMutation(
        scriptCommentsMutations.update(scriptId, comment.id),
    );
    const { mutate: deleteComment, isPending: isDeleting } = useMutation(
        scriptCommentsMutations.delete(scriptId, comment.id),
    );

    const handleSaveEdit = useCallback(() => {
        const body = editBody.trim();
        if (!body) return;
        updateComment({ body }, { onSuccess: () => setEditMode(false) });
    }, [editBody, updateComment]);

    const handleCancelEdit = useCallback(() => {
        setEditBody(comment.body);
        setEditMode(false);
    }, [comment.body]);

    const scriptPath = comment.script?.project_id
        ? `/projects/${comment.script.project_id}/scripts/${comment.script.id}`
        : null;

    return (
        <Card
            size="small"
            style={{ marginBottom: token.marginMD }}
            styles={{ body: { padding: token.paddingMD } }}
        >
            <Flex vertical gap="small">
                <Flex justify="space-between" align="flex-start" gap="small" wrap="wrap">
                    <Flex vertical gap={2}>
                        {scriptPath ? (
                            <Link to={scriptPath}>
                                <Typography.Text strong style={{ color: token.colorPrimary }}>
                                    {comment.script?.title ?? 'Скрипт'}
                                </Typography.Text>
                            </Link>
                        ) : (
                            <Typography.Text strong>{comment.script?.title ?? 'Скрипт'}</Typography.Text>
                        )}
                        {comment.script?.project_title && (
                            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                                {comment.script.project_title}
                            </Typography.Text>
                        )}
                    </Flex>

                    <Flex gap="small" align="center" wrap="wrap">
                        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                            {comment.created_at}
                        </Typography.Text>
                        {comment.is_edited && (
                            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM - 1 }}>
                                (редаговано)
                            </Typography.Text>
                        )}
                        {comment.is_read ? (
                            <Tag color="success" style={{ margin: 0 }}>прочитано</Tag>
                        ) : (
                            <Tag style={{ margin: 0 }}>не прочитано</Tag>
                        )}
                        {canEdit && !editMode && (
                            <>
                                <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => setEditMode(true)}
                                />
                                <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    loading={isDeleting}
                                    onClick={() => deleteComment()}
                                />
                            </>
                        )}
                    </Flex>
                </Flex>

                {editMode ? (
                    <Flex vertical gap="small">
                        <Input.TextArea
                            value={editBody}
                            onChange={(e) => setEditBody(e.target.value)}
                            autoSize={{ minRows: 2, maxRows: 6 }}
                            maxLength={5000}
                            showCount
                        />
                        <Flex gap="small">
                            <Button
                                type="primary"
                                size="small"
                                loading={isUpdating}
                                onClick={handleSaveEdit}
                            >
                                Зберегти
                            </Button>
                            <Button size="small" onClick={handleCancelEdit}>
                                Скасувати
                            </Button>
                        </Flex>
                    </Flex>
                ) : (
                    <Typography.Paragraph
                        style={{
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}
                    >
                        {comment.body}
                    </Typography.Paragraph>
                )}
            </Flex>
        </Card>
    );
};

export const MyCommentsPage = () => {
    const { token } = theme.useToken();
    const [page, setPage] = useState(1);

    const { data, isLoading } = useQuery(myCommentsQueries.getMyComments(page, PER_PAGE));

    const comments = data?.data ?? [];
    const total = data?.meta?.total ?? 0;

    return (
        <>
            <Helmet>
                <title>Мої коментарі</title>
            </Helmet>

            <Row justify="center">
                <Col xs={24} sm={24} md={20} lg={16} xl={14}>
                    <Typography.Title level={4} style={{ marginBottom: token.marginLG }}>
                        Мої коментарі
                    </Typography.Title>

                    {isLoading && (
                        <Flex vertical gap="middle">
                            {[1, 2, 3].map((i) => (
                                <Card key={i} size="small">
                                    <Skeleton active paragraph={{ rows: 2 }} />
                                </Card>
                            ))}
                        </Flex>
                    )}

                    {!isLoading && comments.length === 0 && (
                        <Card>
                            <Typography.Text type="secondary">
                                Ви ще не залишили жодного коментаря
                            </Typography.Text>
                        </Card>
                    )}

                    {!isLoading && comments.length > 0 && (
                        <>
                            {comments.map((comment) => (
                                <MyCommentCard key={comment.id} comment={comment} />
                            ))}

                            {total > PER_PAGE && (
                                <Flex justify="center" style={{ marginTop: token.marginMD }}>
                                    <Pagination
                                        current={page}
                                        pageSize={PER_PAGE}
                                        total={total}
                                        onChange={setPage}
                                        showSizeChanger={false}
                                    />
                                </Flex>
                            )}
                        </>
                    )}
                </Col>
            </Row>
        </>
    );
};
